import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, filter, map } from 'rxjs';
import { DateFormatService } from '../date/date-format.service';
import { CAcceptableDataSources, IPostMessageData, IReceiveMessageData } from '../window-message/window-message.model';
import { WindowMessageService } from '../window-message/window-message.service';

@Injectable({
  providedIn: 'root'
})
export class IframeService {
  private isInIframe = false;
  private isIframeVisible = false;
  private origin = '';
  public topOfPageVisible = false;
  public scrollingValue: IPostMessageData['scrollingValue'];
  private currentIFrameMessage$: BehaviorSubject<IReceiveMessageData | undefined> = new BehaviorSubject<
    IReceiveMessageData | undefined
  >(undefined);

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private windowMessageService: WindowMessageService,
    private dateFormatService: DateFormatService
  ) {
    this.isInIframe = this.isInsideIframe();
  }

  /**
   * Broadcast to host that the CX3 is ready
   */
  broadcastCheckoutReady(): void {
    if (!this.isInIframe) {
      return;
    }

    this.windowMessageService.postMessage('parent', {
      action: 'ready',
      value: true
    } as IPostMessageData);
  }

  /**
   * Broadcast to host to toggle iframe's 'scrolling' value
   * @param scrollingValue indicating when the browser should provide a scrollbar for the frame
   */
  broadcastIframeScrolling(scrollingValue: IPostMessageData['scrollingValue']): void {
    if (!this.isInIframe) {
      return;
    }

    this.scrollingValue = scrollingValue;

    this.windowMessageService.postMessage('parent', {
      action: 'scrolling',
      scrollingValue
    } as IPostMessageData);
  }

  /**
   * Broadcast wether the top of the current checkout page is visible
   * @param visible a boolean representing weather the top of the checkout is visible
   */
  broadcastTopOfPageVisible(visible: boolean): void {
    if (!this.isInIframe) {
      return;
    }

    this.topOfPageVisible = visible;
    this.windowMessageService.postMessage('parent', {
      action: 'topOfPageVisible',
      value: visible
    } as IPostMessageData);
  }

  /**
   * Broadcast to host that the waiver has been visited through the post purchase flow
   */
  broadcastWaiverStarted(): void {
    if (!this.isInIframe) {
      return;
    }

    this.windowMessageService.postMessage('parent', { action: 'waiverStarted' } as IPostMessageData);
  }

  /**
   * Filters post messages only from foreign origins and maps it to a usable data interface
   * @returns observable of usable post message data
   */
  getMessageData(): Observable<IReceiveMessageData> {
    return this.windowMessageService.receiveMessage().pipe(
      filter(
        (event: MessageEvent) =>
          event.origin !== this.document.location.origin && CAcceptableDataSources.includes(event.data?.source || '')
      ),
      map((event: MessageEvent) => {
        let data: IReceiveMessageData = {
          productId: event.data?.productId,
          isVisible: event.data?.isVisible,
          consent: event.data?.consent,
          date: event.data?.date,
          section: event.data?.section,
          sessionStart: event.data?.sessionStart,
          sessionDate: event.data?.sessionDate,
          code: event.data?.code
        };

        if (event.data?.date instanceof Date) {
          data = { ...data, date: this.dateFormatService.dateToStringDateOnlyHyphen(event.data?.date) };
        }

        if (data.isVisible !== undefined) {
          this.isIframeVisible = data.isVisible;
          this.origin = event.origin;
        }

        this.setCurrentIFrameMessage(data);
        return data;
      })
    );
  }

  /**
   * Gets the current subject containing the navigateToProduct. Only emits when it has value.
   * @returns Observable of the navigateToProduct items.
   */
  getCurrentIFrameMessage(): Observable<IReceiveMessageData | undefined> {
    return this.currentIFrameMessage$.asObservable();
  }

  /**
   * Sets the subject with the navigateToProduct value.
   */
  setCurrentIFrameMessage(value: IReceiveMessageData): void {
    this.currentIFrameMessage$.next(value);
  }

  /** @returns if the app is running in an iframe */
  getIsInsideIframe(): boolean {
    return this.isInIframe;
  }

  /** @returns boolean of the last recorded visibility state of the parent iframe */
  getIsIframeVisible(): boolean {
    return this.isIframeVisible;
  }

  /** @returns the domain of the parent iframe */
  getIframeOrigin(): string | undefined {
    return this.isInsideIframe() ? this.origin : undefined;
  }

  /**
   * Uses window comparison to detect if we are inside an iframe.
   * This check only needs to happen once on-load as this doesn't change during the apps lifecycle
   */
  private isInsideIframe(): boolean {
    return window && window.self !== window.top;
  }
}
